import {
  ConnectorGenericInputStructureType,
  ConnectorGenericInputStructureItemType,
  ConnectorGenericOutputStructureType,
} from "../../types";
import _ from "lodash";


export const CONNETTORI_LEGNO_INPUT_STRUCTURE: ConnectorGenericInputStructureType = {
  __: {  // Unnamed group: these parameters are not grouped
    span: {},  // direct question
    support: {},  // direct question
    assitoType: {},  // direct question
    assitoThickness: {
      questions: {
        thickness: {
          label: {
            id: "wcnn_assito_thickness_question_helper",
            defaultMessage: "Domanda diretta per lo spessore dell'assito",
          },
        }
      },
      compute: ({thickness}) => thickness || 0,
    },
    connectorSpacing: {},  // direct question
    connectorOrientation: {  // direct question
      default: "45",
    },
    beamBase: {},  // direct question
    beamHeight: {},  // direct question
    beamInteraxis: {},  // direct question
    concreteThickness: {},  // direct question
    toothWidth: {
      questions: {
        tooth: {
          label: {
            id: "wcnn_tooth_width_question_helper",
            defaultMessage: "Domanda diretta per la larghezza del dente",
          },
        },
        assitoType: {
          label: {
            id: "wcnn_assito_type_question_helper",
            defaultMessage: "Domanda diretta per il tipo di assito",
          }
        },
      },
      compute: ({tooth, assitoType}) => {
        if (assitoType !== "Interrupted")
          return null;
        return tooth;
      }
    }
  },
  loads: {
    initialDeflectionLimit: {},  // direct question
    finalDeflectionLimit: {},  // direct question
    g1: {},  // direct question
    g2: {},  // direct question
    q: {},  // direct question
    psi: {},  // direct question
    serviceClass: {},  // direct question
    loadDurationClass: {},  // direct question
  },
  woodType: {
    type: {},  // direct question
    name: {},  // direct question
    ...(["fmk", "ft0k", "fc0k", "fvk", "e0Mean", "gamma", "rhoK"].reduce(
      (acc, key) => ({...acc, [key]: ({
        questions: {
          type: {
            label: {
              id: "wcnn_wood_type_question_helper",
              defaultMessage: "Categoria di travi scelta dall'utente",
            }
          },
          name: {
            label: {
              id: "wcnn_wood_name_question_helper",
              defaultMessage: "Classe di resistenza scelta dall'utente",
            }
          }
        },
        compute: ({type, name}, csvs) => {
          const data = csvs!["timbertechWoodMaterials"];
          const row = data.find((row) => row.type === type && row.name === name);
          if (!row)
            throw new Error(`Cannot find row for type: ${type}, name: ${name} (wood materials)`);
          const result: string | undefined = row[key];
          if (result === undefined)
            throw new Error(`Cannot find value for key: ${key} (wood materials)`);
          return parseFloat(result);
        },
      } as ConnectorGenericInputStructureItemType)}),
      {},
    )),
  },
  concreteType: {
    name: {
      questions: {
        reinforcement: {
          label: {
            id: "wcnn_reinforcement_question_helper",
            defaultMessage: "Tipo di armatura scelta dall'utente",
          }
        },
        class_: {
          label: {
            id: "wcnn_concrete_name_question_helper",
            defaultMessage: "Classe di resistenza CA scelta dall'utente (non presente se viene selezionato un prodotto Kerakoll per il rinforzo)",
          }
        },
      },
      compute: ({reinforcement, class_}) => class_ ?? reinforcement,
    },
    ...(["type", "fck", "rck", "fctk", "ecm", "gamma"].reduce(
      (acc, key) => ({...acc, [key]: ({
        questions: {
          reinforcement: {
            label: {
              id: "wcnn_reinforcement_question_helper",
              defaultMessage: "Tipo di rinforzo scelto dall'utente",
            }
          },
          class_: {
            label: {
              id: "wcnn_concrete_name_question_helper",
              defaultMessage: "Classe di resistenza CA scelta dall'utente (non presente se viene selezionato un prodotto Kerakoll per il rinforzo)",
            }
          },
        },
        compute: ({reinforcement, class_}, csvs) => {
          const data = csvs!["timbertechCA"];
          const name = class_ ?? reinforcement;
          const row = data.find((row) => row.name === name);
          if (!row)
            throw new Error(`Cannot find row for name: ${name} (wood CA)`);
          const result = row[key];
          if (result === undefined)
            throw new Error(`Cannot find value for key: ${key} (wood CA)`);
          return key === "type" ? result : parseFloat(result);
        },
      } as ConnectorGenericInputStructureItemType)}),
      {},
    )),
  },
}

export const CONNETTORI_LEGNO_OUTPUT_SCHEME: Record<string, ConnectorGenericOutputStructureType> = {
  floor: {
    type: "object",
    fields: {
      loads: {
        type: "object",
        fields: {
          initialDeflectionLimit: {type: "field"},
          finalDeflectionLimit: {type: "field"},
          g1: {type: "field"},
          g2: {type: "field"},
          q: {type: "field"},
          psi: {type: "field"},
          serviceClass: {type: "field"},
          loadDurationClass: {type: "field"},
        },
      },
      span: {type: "field"},
      support: {type: "field"},
      assitoType: {type: "field"},
      assitoThickness: {type: "field"},
      connectorSpacing: {type: "field"},
      connectorOrientation: {type: "field"},
      beamBase: {type: "field"},
      beamHeight: {type: "field"},
      beamInteraxis: {type: "field"},
      woodType: {
        type: "object",
        fields: {
          type: {type: "field"},
          name: {type: "field"},
          fmk: {type: "field"},
          ft0k: {type: "field"},
          fc0k: {type: "field"},
          fvk: {type: "field"},
          e0Mean: {type: "field"},
          gamma: {type: "field"},
          rhoK: {type: "field"},
        },
      },
      concreteThickness: {type: "field"},
      toothWidth: {type: "field"},
      concreteType: {
        type: "object",
        fields: {
          name: {type: "field"},
          fck: {type: "field"},
          rck: {type: "field"},
          fctk: {type: "field"},
          ecm: {type: "field"},
          gamma: {type: "field"},
        },
      },
    }
  },
  results: {
    type: "list",
    fields: {
      connector: {
        type: "object",
        fields: {
          name: {type: "field"},
          description: {type: "field"},
          values: {
            type: "object",
            fields: {
              k_ser: {type: "field"},
              k_u: {type: "field"},
              f_uk: {type: "field"},
            },
          },
        },
      },
      connector90: {
        type: "object",
        fields: {
          name: {type: "field"},
          description: {type: "field"},
          values: {
            type: "object",
            fields: {
              k_ser: {type: "field"},
              k_u: {type: "field"},
              f_uk: {type: "field"},
            },
          },
        },
      },
      connectorCounterCentralPart: {type: "field"},
      connectorCounterExternalPart: {type: "field"},
      connectorCounter: {type: "field"},
      connector90Counter: {type: "field"},
      pMin: {type: "field"},
      pMax: {type: "field"},
      borderConnectorDistance: {type: "field"},
      resistance: {
        type: "object",
        fields: {
          fcd: {type: "field"},
          fctd: {type: "field"},
          ft0d: {type: "field"},
          fmd: {type: "field"},
          frd: {type: "field"},
          frd90: {type: "field"},
          fvd: {type: "field"},
          kcr: {type: "field"},
        },
      },
      loads: {
        type: "object",
        fields: {
          lcalc: {type: "field"},
          g1k: {type: "field"},
          g2k: {type: "field"},
          qk: {type: "field"},
          qslu: {type: "field"},
          qsle: {type: "field"},
        },
      },
      geometry: {
        type: "object",
        fields: {
          acls: {type: "field"},
          ygcls: {type: "field"},
          jcls: {type: "field"},
          wclssup: {type: "field"},
          wclsinf: {type: "field"},
          atr: {type: "field"},
          ygtr: {type: "field"},
          jtr: {type: "field"},
          wtr: {type: "field"},
          concreteWidth: {type: "field"},
        },
      },
      instantaneousSluStiffness: {
        type: "object",
        fields: {
          a2: {type: "field"},
          a1: {type: "field"},
          gamma1: {type: "field"},
          eJEff: {type: "field"},
        },
      },
      instantaneousSlu: {
        type: "object",
        fields: {
          mslu: {type: "field"},
          vslu: {type: "field"},
          vsed: {type: "field"},
          vsed90: {type: "field"},
          sccls: {type: "field"},
          stcls: {type: "field"},
          sttr: {type: "field"},
          smtr: {type: "field"},
          ttr: {type: "field"},
        },
      },
      instantaneousSluVerification: {
        type: "object",
        fields: {
          clsCompression: {type: "field"},
          clsTraction: {type: "field"},
          clsArmor: {type: "field"},
          clsTractionDescription: {type: "field"},
          beamBending: {type: "field"},
          beamShear: {type: "field"},
          connector: {type: "field"},
          connector90: {type: "field"},
        },
      },
      finalSluStiffness: {
        type: "object",
        fields: {
          a2: {type: "field"},
          a1: {type: "field"},
          gamma1: {type: "field"},
          eJEff: {type: "field"},
        },
      },
      finalSlu: {
        type: "object",
        fields: {
          mslu: {type: "field"},
          vslu: {type: "field"},
          vsed: {type: "field"},
          vsed90: {type: "field"},
          sccls: {type: "field"},
          stcls: {type: "field"},
          sttr: {type: "field"},
          smtr: {type: "field"},
          ttr: {type: "field"},
        },
      },
      finalSluVerification: {
        type: "object",
        fields: {
          clsCompression: {type: "field"},
          clsTraction: {type: "field"},
          clsArmor: {type: "field"},
          clsTractionDescription: {type: "field"},
          beamBending: {type: "field"},
          beamShear: {type: "field"},
          connector: {type: "field"},
          connector90: {type: "field"},
        },
      },
      instantaneousSleStiffness: {
        type: "object",
        fields: {
          a2: {type: "field"},
          a1: {type: "field"},
          gamma1: {type: "field"},
          eJEff: {type: "field"},
        },
      },
      instantaneousSle: {
        type: "object",
        fields: {
          f: {type: "field"},
          ug: {type: "field"},
          uq: {type: "field"},
          u0: {type: "field"},
          verification: {type: "field"},
          limit: {type: "field"},
        },
      },
      finalSleStiffness: {
        type: "object",
        fields: {
          a2: {type: "field"},
          a1: {type: "field"},
          gamma1: {type: "field"},
          eJEff: {type: "field"},
        },
      },
      finalSle: {
        type: "object",
        fields: {
          f: {type: "field"},
          ug: {type: "field"},
          uq: {type: "field"},
          u0: {type: "field"},
          verification: {type: "field"},
          limit: {type: "field"},
        },
      },
      regulationCoefficients: {
        type: "object",
        fields: {
          kmod: {type: "field"},
          kdef: {type: "field"},
          gamma_g1: {type: "field"},
          gamma_g2: {type: "field"},
          gamma_q: {type: "field"},
          gamma_mtr: {type: "field"},
          gamma_mc: {type: "field"},
          gamma_mconn: {type: "field"},
        },
      },
    },
  },
  selected: {type: "field"},
}